// The browser client for the owner's native setup of what a saved function may change in the App's data:
// POST /api/v1/company/apps/:artifactId/functions/:functionId/allow-data-writes with the exact selected version
// and revision, one retained operation id, who may use it and the explicit collection/kind choices. The server
// (owner, current selection, declared collections and current rights) is the only authority; nothing here
// accepts a success it did not state.
import type { WriteKind } from './control-selection-api';

export type FunctionSetupRequest = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  functionId: string;
  operationId: string;
  actorScope: 'owner-only' | 'app-members';
  collectionEffects: { collectionId: string; kinds: WriteKind[] }[];
};
export type FunctionSetupOutcome =
  | { kind: 'ok' }
  | { kind: 'refused'; reason: 'not_authorized' | 'changed' | 'invalid' }
  | { kind: 'unknown' };
export interface FunctionSetupApi {
  allow(request: FunctionSetupRequest): Promise<FunctionSetupOutcome>;
}

export function createFunctionSetupApi(send: typeof fetch): FunctionSetupApi {
  return {
    async allow({ artifactId, functionId, ...body }) {
      let response: Response;
      try {
        response = await send(
          `/api/v1/company/apps/${encodeURIComponent(artifactId)}/functions/${encodeURIComponent(functionId)}/allow-data-writes`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
          },
        );
      } catch {
        return { kind: 'unknown' };
      }
      let answer: { error?: unknown; ok?: unknown } | null = null;
      try {
        answer = await response.json();
      } catch {
        answer = null;
      }
      if (response.status === 200)
        return answer?.ok === true ? { kind: 'ok' } : { kind: 'unknown' };
      if (response.status === 403) return { kind: 'refused', reason: 'not_authorized' };
      if (response.status === 409)
        return answer?.error === 'function_setup_outcome_unknown'
          ? { kind: 'unknown' }
          : { kind: 'refused', reason: 'changed' };
      if (response.status === 400 || response.status === 422)
        return { kind: 'refused', reason: 'invalid' };
      return { kind: 'unknown' };
    },
  };
}
